/**
 * Alpine component for the AGB (Terms & Conditions) editor in the website
 * legal tab. Drives reactive marker-count UI: the per-locale "still N
 * placeholders" hint and the "AGB ist fertig" checkbox + submit button enable
 * state are computed from the editor's LIVE content — no save needed for the
 * hint or checkbox to update.
 *
 * Wired up in templates/components/legal_agb_editor.templ. The MarkdownEditor's
 * hidden input dispatches a `markdown-change` custom event on every keystroke
 * (debounced 100ms in static/js/markdown-editor/src/alpine-component.ts);
 * we listen on the form-level scope so any per-locale editor's change updates
 * the shared `content` map.
 *
 * IMPORTANT: The marker regex below MUST stay in sync with the Go regex in
 * internal/website/legal/agb/markers.go (`markerPattern`). Both match
 * `[Bitte ergänzen:...]` and `[Please fill in:...]` (DE+EN), case-sensitive,
 * with a leading non-backslash boundary so `\[escaped\]` is skipped.
 */

// markerRegex mirrors internal/website/legal/agb/markers.go.
// Capture group 1 = the label string between the colon and the closing bracket.
const markerRegex = /(?:^|[^\\])\[(?:Bitte ergänzen|Please fill in):([^\]]*)\]/g;

interface MarkerTemplates {
  // Pluralized "N placeholders remaining" header. Both the singular and
  // plural forms are pre-resolved Go-side via go-i18n so the right plural
  // rule is applied per language. The plural form is rendered with a
  // sentinel count (matching agbMarkerPlaceholderCount in the Go-side
  // legal_agb_editor.templ) which the JS substitutes with the LIVE count.
  // remainingPlaceholder is the literal string the JS swaps (kept as data
  // rather than hardcoded so the Go side stays the single source of
  // truth).
  remainingOne: string;
  remainingOther: string;
  remainingPlaceholder: string;
  // Success message when count == 0 ("AGB ist vollständig").
  done: string;
}

interface LegalAGBEditorInit {
  primary: string;
  locales: string[];
  // Per-locale initial markdown content, seeded from the server-rendered
  // hidden inputs in each per-locale form.
  initialContent: Record<string, string>;
  // i18n labels for the hint, pre-rendered server-side (resolved per
  // request user lang). Kept JS-side so we don't have to wire a JS i18n
  // runtime into the bundle.
  labels: MarkerTemplates;
}

export function legalAGBEditor(init: LegalAGBEditorInit) {
  return {
    locale: init.primary,
    locales: init.locales,
    // Reactive per-locale content. Seeded from server-rendered values.
    // Mutations to a nested key require this.content = { ...this.content, [k]: v }
    // for Alpine to detect the change — see updateLocaleContent below.
    content: { ...init.initialContent } as Record<string, string>,
    labels: init.labels,

    init() {
      // Listen for the markdown editor's debounced change event. The
      // event bubbles from each per-locale `<input type="hidden">` so
      // we catch all of them at the wrapper level. We discover which
      // locale the input belongs to via the hidden `locale` input in
      // the same form.
      const root = (this as any).$el as HTMLElement;
      root.addEventListener("markdown-change", (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        if (!target || target.name !== "agb_md") return;
        const form = target.closest("form") as HTMLFormElement | null;
        if (!form) return;
        const localeInput = form.querySelector('input[name="locale"]') as HTMLInputElement | null;
        const loc = localeInput?.value || this.locale;
        this.updateLocaleContent(loc, target.value || "");
      });
    },

    updateLocaleContent(loc: string, md: string) {
      // Replace whole map so Alpine reactivity fires on getter recompute.
      this.content = { ...this.content, [loc]: md };
    },

    countMarkers(md: string): number {
      if (!md) return 0;
      // Need a fresh regex (state-bearing /g flag).
      const re = new RegExp(markerRegex.source, "g");
      const matches = md.match(re);
      return matches ? matches.length : 0;
    },

    listMarkers(md: string): string[] {
      if (!md) return [];
      const re = new RegExp(markerRegex.source, "g");
      const out: string[] = [];
      const seen = new Set<string>();
      let m: RegExpExecArray | null;
      while ((m = re.exec(md)) !== null) {
        const label = (m[1] || "").trim();
        if (seen.has(label)) continue;
        seen.add(label);
        out.push(label);
      }
      return out;
    },

    get currentMarkers(): string[] {
      return this.listMarkers(this.content[this.locale] || "");
    },

    get currentCount(): number {
      return this.countMarkers(this.content[this.locale] || "");
    },

    get currentMarkersJoined(): string {
      return this.currentMarkers.join(", ");
    },

    // this.locales is seeded primary-locale-only (see legalAGBInitJSON /
    // legal_agb_editor.templ) so this only ever checks the primary
    // editor's content — matches the server-side guard in
    // MarkAGBReviewed and the readiness-dot/completeness logic, which
    // also gate AGB on the primary locale only.
    get anyMarkers(): boolean {
      return this.countMarkers(this.content[this.locales[0]] || "") > 0;
    },

    // remainingLabel returns the pluralized "N placeholders remaining"
    // string for the current locale's content. The Go side renders both
    // forms via go-i18n; the plural form embeds the sentinel
    // `remainingPlaceholder` value which we swap for the live count.
    get remainingLabel(): string {
      const n = this.currentCount;
      if (n === 1) return this.labels.remainingOne;
      return this.labels.remainingOther.split(this.labels.remainingPlaceholder).join(String(n));
    },
  };
}
